/**
 * @file Chapter reducer — objective progression (#629) + the Part-boundary
 *       assessment controller (umbrella #1357).
 * @module @labcraft/course-kit/runtime/reducer
 *
 * Pure state machine driven by these action types:
 *
 *   - `objective.progressed`: a command in the active objective's
 *     sequence matched, with required commands still outstanding.
 *     Records the matched index, moves the intra-objective pointer past
 *     it. The transcript PERSISTS (#1566) — mid-objective recovery leaves
 *     the hook + nudges visible as the objective's history.
 *   - `objective.completed`: the sequence's last outstanding required
 *     command matched (or the reader clicked "I did it" for a `manual`
 *     objective). Marks the objective complete, advances past any
 *     already-completed objectives, resets the intra-objective pointer, and
 *     MOVES a non-empty transcript into the transient `capstone` (#1566) so
 *     a late encouragement can still land.
 *   - `objective.wrongTurn`: a `wrongTurn.detect` matched on the active
 *     objective. Appends the authored `mentorHook` as a `hook` transcript
 *     entry (deduped by `wrongTurnId`) and sets pending `standing-by`. Never
 *     clobbers a prior entry (folds #1569); never blocks completion — a
 *     subsequent satisfying command still advances (Build Spec decision 1).
 *   - `mentor.disposition`: a mentor disposition arrived (#1566, mentor →
 *     lab → SPA). `held` → pending `standing-by` (no entry); `nudge` /
 *     `encouragement` → append a transcript entry (encouragement for a
 *     just-completed objective lands in the capstone); everything else →
 *     resolve pending to `quiet`. Advisory only — never advances or gates.
 *   - `mentor.pending`: the lab-owned "AI looking…" beat (#1566) — a real
 *     observation relay, so a model call is coming. Sets pending `looking`.
 *   - `capstone.cleared`: the provider's short hold timer fired (#1566) —
 *     drop the capstone.
 *   - `command.observed`: any command ran on the pty — increments the
 *     `commandCount` the mentor dock shows as "watching · N commands" (#939).
 *   - `spec.replaced`: a new chapter mounted. Resets ALL objective
 *     progress, optionally re-seeded with hydrated completions (#1493).
 *
 * ## The knowledge-check flip (umbrella #1357)
 *
 * The per-chapter adaptive quiz that used to run after the objectives
 * phase is retired: mastery is now assessed **once at the Part boundary**
 * by a Computerized Adaptive Test over the whole Part's consolidated bank.
 * So this reducer no longer carries a quiz phase — a chapter's **phase**
 * is just `objectives → complete` ({@link chapterPhase}), completing
 * straight off the objective index. The pure CAT machinery that used to
 * live here as `quiz.*` actions is relocated onto {@link
 * PartAssessmentController}, which the lab `PartAssessmentPage` drives off
 * a `QuizState` it owns (there is no reducer action for it — the page
 * threads `QuizEngine.init` / `apply` / `grade` directly). The
 * {@link QuizEngine} itself is unchanged.
 */

import type { ChapterSpec, PartAssessment, QuizQuestion } from '../types.ts'
import type { CourseGraph, NodeId } from '../graph_types.ts'
import {
  QuizEngine,
  type QuizConfig,
  type QuizDecision,
  type QuizState,
  type QuizSubmission,
} from './quiz_engine.ts'

/**
 * The default operator-pinned {@link QuizConfig} the lab `PartAssessmentPage`
 * passes when driving a Part's CAT assessment. These are umbrella #1203's
 * recommended starting values, carried forward to the Part boundary — the
 * operator owns them, so this constant lives at the (now shared) call-site
 * layer rather than baked into a schema default (`design.md`: "Defaults
 * belong to the operator"). A caller overrides any subset before handing it
 * to {@link PartAssessmentController.initState}; `rng` defaults to
 * `Math.random` and tests inject a deterministic stub.
 *
 * `weight` is the linear identity (`d => d`); `startBand` here is the
 * operator floor — a `PartAssessment.startBand` override, when set,
 * supersedes it inside {@link PartAssessmentController.initState}.
 */
export const DEFAULT_QUIZ_CONFIG: QuizConfig = {
  weight: d => d,
  startBand: 3,
  max: 24,
  passThreshold: 0.7,
  minQuestions: 4,
  maxQuestions: 12,
  priorWrongDecay: 0.8,
  interleaveRatio: 0.25,
  rng: Math.random,
}

/**
 * The dock's honest-pending state (#1566). Drives the "is the AI doing
 * something?" line under the transcript:
 *
 *   - `standing-by` — an authored hook covers the learner and the mentor is
 *     holding (disposition `held`); the hook line is the guidance, no model
 *     call is coming yet.
 *   - `looking` — the lab relayed a real observation, so a model call IS
 *     coming (the lab-owned "AI looking…" beat); resolved by the next
 *     disposition.
 *   - `quiet` — nothing pending. The steady state.
 */
export type MentorPending = 'quiet' | 'standing-by' | 'looking'

/**
 * The mentor dispositions the dock reacts to (#1566) — the lab relays every
 * disposition on its `mentor` frame; the reducer maps them: `held` →
 * `standing-by` pending (no entry, an authored hook covers it), `nudge` /
 * `encouragement` → append a transcript entry, everything else → resolve
 * pending to `quiet` (no entry). A course-kit-local union (course-kit is
 * course-agnostic and does not import `@labcraft/mentor`); the lab maps its
 * wire disposition onto it verbatim.
 */
export type MentorDockDisposition =
  | 'held'
  | 'silent'
  | 'nudge'
  | 'encouragement'
  | 'budget_exceeded'
  | 'error'

/**
 * One entry in an objective's mentor transcript (#1566). The dock renders
 * an objective's entries oldest→newest: authored `hook`s (trusted HTML,
 * no AI — the former standalone `rethink.mentorHook`, so a live nudge no
 * longer clobbers the curated line — folds #1569), AI `nudge`s (plain text,
 * rung-labelled), and the `encouragement` capstone (plain text,
 * success-after-struggle). `kind` selects the dock's render path
 * (trusted-HTML vs escaped plain text) and mirrors the renderable subset of
 * the mentor capture disposition set (`held`→`hook`, `nudge`,
 * `encouragement`). Part 7 collapses today's separate single-slot `rethink`
 * + `nudge` into an append-only list of these (the type landed in part 1 so
 * every layer agrees on the shape).
 *
 * @property kind - which entry this is; selects the dock's render path.
 * @property objectiveId - the objective this entry belongs to (the
 *   transcript is scoped per objective).
 * @property wrongTurnId - `hook` only: which `WrongTurn` fired; the
 *   dedupe key for a re-fired identical hook.
 * @property html - `hook` only: the authored `mentorHook`, rendered as
 *   trusted HTML (authored content, not model output).
 * @property rung - `nudge` only: the restraint-ladder rung (1 = nudge,
 *   2 = reframe, 3 = shape); drives the escalating label.
 * @property text - `nudge` / `encouragement`: the plain-text line (model
 *   output — never rendered as HTML; XSS discipline).
 */
export type TranscriptEntry =
  | { kind: 'hook'; objectiveId: string; wrongTurnId: string; html: string }
  | { kind: 'nudge'; objectiveId: string; rung: number; text: string }
  | { kind: 'encouragement'; objectiveId: string; text: string }

/**
 * The transient capstone slot (#1566): when an objective completes with a
 * non-empty transcript, its entries move here (keyed by the completed
 * `objectiveId`) rather than being dropped — completion still advances the
 * index immediately, so progression is never delayed, and the async
 * success-after-struggle `encouragement` (which lands AFTER completion)
 * appends here and shows briefly. Cleared on a short timer (the provider's
 * `capstone.cleared` dispatch) or when the next objective's first entry
 * arrives; a too-late encouragement (for neither the active transcript nor
 * this capstone) is dropped as stale.
 *
 * @property objectiveId - the completed objective these entries belong to;
 *   the key a late `encouragement` must match to append.
 * @property entries - the moved transcript, oldest→newest.
 */
export interface CapstoneState {
  objectiveId: string
  entries: TranscriptEntry[]
}

/**
 * The two-valued chapter phase (umbrella #1357). `objectives` is the
 * hands-on terminal walk; `complete` is the terminal state, reached
 * straight from `objectives` once every objective is done. There is no
 * longer a `quiz` phase — mastery moved to the Part-boundary assessment. A
 * derived value ({@link chapterPhase}), never a stored field.
 */
export type ChapterPhase = 'objectives' | 'complete'

/**
 * Reducer state shape. Held inside `<ChapterProvider/>`'s `useReducer`.
 *
 * @property spec - the spec the provider was mounted with
 * @property currentObjectiveIndex - 0-based index into
 *   `spec.objectives`. Past the length means the objectives phase is done —
 *   the chapter is `complete`.
 * @property currentCommandIndex - 0-based index into the **active**
 *   objective's `commands`: the first not-yet-matched command. Resets
 *   to 0 whenever the active objective changes.
 * @property completedObjectiveIds - ids of every satisfied objective
 *   (including out-of-order completions)
 * @property satisfiedCommands - per-objective set of `commands` indices
 *   that actually matched, so a done card can show which optional
 *   commands the learner ran versus skipped
 * @property transcript - the active objective's append-only mentor
 *   transcript (#1566): authored hooks + AI nudges/encouragement, oldest→
 *   newest. All entries share the active objective's id; reset when the
 *   objective changes. Clears on completion (moves to `capstone`) and
 *   `spec.replaced`, NOT on mid-objective recovery.
 * @property capstone - the transient post-completion slot (#1566), or
 *   `null`. Holds a completed objective's transcript so a late
 *   `encouragement` can still land + show; see {@link CapstoneState}.
 * @property pending - the dock's honest-pending state (#1566); see
 *   {@link MentorPending}.
 * @property commandCount - total commands the learner has run this
 *   chapter, for the mentor dock's "watching · N commands" state (#939)
 */
export interface ChapterState {
  spec: ChapterSpec
  currentObjectiveIndex: number
  currentCommandIndex: number
  completedObjectiveIds: Set<string>
  satisfiedCommands: Map<string, ReadonlySet<number>>
  transcript: TranscriptEntry[]
  capstone: CapstoneState | null
  pending: MentorPending
  commandCount: number
}

/**
 * Discriminated action union for {@link chapterReducer}. All variants are
 * objective-progression or lifecycle beats — the reducer holds no engine
 * deps, keeping it React-, graph-, and I/O-free.
 *
 * @property type - discriminator; see the file header for each variant's
 *   semantics
 * @property id - `objective.completed` only: the completed objective's id
 * @property objectiveId - `objective.wrongTurn` / `mentor.disposition` /
 *   `mentor.pending` only: the objective the action is scoped to
 * @property wrongTurnId - `objective.wrongTurn` only: which `WrongTurn` fired
 * @property mentorHook - `objective.wrongTurn` only: the mentor's opening line
 * @property disposition - `mentor.disposition` only: the mentor's disposition
 * @property rung - `mentor.disposition` only: the restraint-ladder rung
 *   (present on a `nudge`)
 * @property text - `mentor.disposition` only: the nudge/encouragement text
 *   (present on a text-bearing disposition)
 * @property spec - `spec.replaced` only: the newly-mounted chapter spec
 * @property completed - `spec.replaced` only: already-completed objective
 *   ids to seed the new chapter's progress (#1493 hydration)
 * @property commandIndex - `objective.progressed` / `objective.completed`
 *   only: the matched command index
 */
export type ChapterAction =
  | {
      type: 'objective.progressed'
      id: string
      commandIndex: number
    }
  | {
      type: 'objective.completed'
      id: string
      commandIndex?: number
    }
  | {
      type: 'objective.wrongTurn'
      objectiveId: string
      wrongTurnId: string
      mentorHook: string
    }
  | {
      type: 'mentor.disposition'
      objectiveId: string
      disposition: MentorDockDisposition
      rung?: number
      text?: string
    }
  | {
      type: 'mentor.pending'
      objectiveId: string
    }
  | {
      type: 'capstone.cleared'
    }
  | {
      type: 'command.observed'
    }
  | {
      type: 'spec.replaced'
      spec: ChapterSpec
      completed?: readonly string[]
    }

/**
 * Build a fresh reducer state from a chapter spec, optionally seeded with
 * already-completed objective ids (#1493 hydration). Seeded ids not present
 * in the spec are dropped (stale content), and `currentObjectiveIndex`
 * advances past the leading run of completed objectives so a resumed
 * chapter opens on the first unfinished beat rather than objective 0.
 *
 * @param spec - the chapter spec to initialise progress for
 * @param seededCompleted - already-completed objective ids to restore, or
 *   undefined for a fresh (unhydrated) start
 * @returns a fresh {@link ChapterState}
 */
export function initialState(
  spec: ChapterSpec,
  seededCompleted?: readonly string[],
): ChapterState {
  const objectiveIds = new Set(spec.objectives.map(o => o.id))
  const completedObjectiveIds = new Set(
    (seededCompleted ?? []).filter(id => objectiveIds.has(id)),
  )
  // Open on the first UNfinished beat: skip the leading run of already-
  // completed objectives (a hydrated resume, not a fresh start).
  let currentObjectiveIndex = 0
  while (
    currentObjectiveIndex < spec.objectives.length &&
    completedObjectiveIds.has(spec.objectives[currentObjectiveIndex]!.id)
  ) {
    currentObjectiveIndex++
  }
  return {
    spec,
    currentObjectiveIndex,
    currentCommandIndex: 0,
    completedObjectiveIds,
    satisfiedCommands: new Map(),
    transcript: [],
    capstone: null,
    pending: 'quiet',
    commandCount: 0,
  }
}

/**
 * Derive the chapter's {@link ChapterPhase} from state (umbrella #1357).
 * The phase is not stored — it falls out of the objective index:
 *
 *   - `complete` — the chapter has objectives AND every one is done (the
 *     index cleared the last objective). Chapter completion is now
 *     objectives-only; the knowledge-check gate moved to the Part boundary.
 *   - `objectives` — otherwise; the learner is still on the terminal walk.
 *
 * @param state - the current chapter state
 * @returns the derived phase
 */
export function chapterPhase(state: ChapterState): ChapterPhase {
  const objectivesDone =
    state.spec.objectives.length > 0 &&
    state.currentObjectiveIndex >= state.spec.objectives.length
  return objectivesDone ? 'complete' : 'objectives'
}

/**
 * Copy `satisfiedCommands` with `commandIndex` added to `id`'s set.
 * Returns the original map untouched when `commandIndex` is undefined
 * (a `manual` completion carries no matched command).
 *
 * @param satisfiedCommands - the current per-objective matched-index map
 * @param id - the objective id whose set gains `commandIndex`
 * @param commandIndex - the matched command index, or undefined for a
 *   `manual` completion (no-op)
 * @returns a new map with the index added, or the original when undefined
 */
function withSatisfiedCommand(
  satisfiedCommands: Map<string, ReadonlySet<number>>,
  id: string,
  commandIndex: number | undefined,
): Map<string, ReadonlySet<number>> {
  if (commandIndex === undefined) return satisfiedCommands
  const next = new Map(satisfiedCommands)
  next.set(id, new Set([...(next.get(id) ?? []), commandIndex]))
  return next
}

/**
 * Append one entry to the active transcript, applying the per-objective
 * scoping (#1566): a new objective's first entry resets the transcript (and
 * clears a lingering capstone for a different objective — the "next
 * objective's first entry" capstone-clear); a re-fired identical hook (same
 * `wrongTurnId`) is deduped; otherwise the entry appends. Returns just the
 * `{ transcript, capstone }` slice for the caller to spread.
 *
 * @param state - the current state
 * @param entry - the entry to push
 * @returns the new `{ transcript, capstone }` pair
 */
function pushEntry(
  state: ChapterState,
  entry: TranscriptEntry,
): Pick<ChapterState, 'transcript' | 'capstone'> {
  const currentId = state.transcript[0]?.objectiveId
  if (currentId !== entry.objectiveId) {
    // New objective's first entry: fresh transcript; drop a capstone for a
    // different objective (a new struggle superseding the last win).
    const capstone =
      state.capstone && state.capstone.objectiveId !== entry.objectiveId
        ? null
        : state.capstone
    return { transcript: [entry], capstone }
  }
  if (
    entry.kind === 'hook' &&
    state.transcript.some(
      e => e.kind === 'hook' && e.wrongTurnId === entry.wrongTurnId,
    )
  ) {
    // Same wrong turn re-fired — the curated line is already showing.
    return { transcript: state.transcript, capstone: state.capstone }
  }
  return { transcript: [...state.transcript, entry], capstone: state.capstone }
}

/**
 * Chapter reducer. Pure; every branch returns a new state object so
 * React's reference-equality bailouts work.
 */
export function chapterReducer(
  state: ChapterState,
  action: ChapterAction,
): ChapterState {
  switch (action.type) {
    case 'objective.progressed': {
      // Progress within an already-completed objective is meaningless.
      if (state.completedObjectiveIds.has(action.id)) return state
      // The transcript PERSISTS through mid-objective recovery (#1566): a
      // hook + its nudges stay visible as the objective's history until the
      // objective is checked off. Progress does not clear it.
      return {
        ...state,
        currentCommandIndex: action.commandIndex + 1,
        satisfiedCommands: withSatisfiedCommand(
          state.satisfiedCommands,
          action.id,
          action.commandIndex,
        ),
      }
    }
    case 'objective.completed': {
      const satisfiedCommands = withSatisfiedCommand(
        state.satisfiedCommands,
        action.id,
        action.commandIndex,
      )
      // Re-completion (a re-run) doesn't advance or move the transcript.
      if (state.completedObjectiveIds.has(action.id)) {
        return { ...state, satisfiedCommands }
      }
      const completed = new Set(state.completedObjectiveIds)
      completed.add(action.id)
      let next = state.currentObjectiveIndex
      while (
        next < state.spec.objectives.length &&
        completed.has(state.spec.objectives[next]!.id)
      ) {
        next++
      }
      // #1566: a non-empty transcript for the completed objective MOVES into
      // the transient capstone (rather than being dropped) so the async
      // success-after-struggle encouragement, which lands after this, still
      // shows. Completion still advances the index immediately — progression
      // is never delayed on the mentor round-trip.
      const movesToCapstone =
        state.transcript.length > 0 &&
        state.transcript[0]!.objectiveId === action.id
      return {
        ...state,
        completedObjectiveIds: completed,
        currentObjectiveIndex: next,
        currentCommandIndex: 0,
        satisfiedCommands,
        transcript: [],
        capstone: movesToCapstone
          ? { objectiveId: action.id, entries: state.transcript }
          : state.capstone,
        pending: 'quiet',
      }
    }
    case 'objective.wrongTurn': {
      // Only meaningful for an active, not-yet-completed objective.
      if (state.completedObjectiveIds.has(action.objectiveId)) return state
      // The authored hook is the FIRST transcript entry — never clobbered by
      // a later nudge (folds #1569). An authored line covers the learner, so
      // the mentor stands by.
      const entry: TranscriptEntry = {
        kind: 'hook',
        objectiveId: action.objectiveId,
        wrongTurnId: action.wrongTurnId,
        html: action.mentorHook,
      }
      return { ...state, ...pushEntry(state, entry), pending: 'standing-by' }
    }
    case 'mentor.disposition': {
      const { objectiveId, disposition } = action
      switch (disposition) {
        case 'held':
          // An authored hook covers it — the mentor is standing by (no model
          // call, no entry). Stale for a done objective → ignore.
          if (state.completedObjectiveIds.has(objectiveId)) return state
          return { ...state, pending: 'standing-by' }
        case 'nudge': {
          // A nudge that raced completion is stale — resolve pending, no entry.
          if (state.completedObjectiveIds.has(objectiveId)) {
            return { ...state, pending: 'quiet' }
          }
          const entry: TranscriptEntry = {
            kind: 'nudge',
            objectiveId,
            rung: action.rung ?? 1,
            text: action.text ?? '',
          }
          return { ...state, ...pushEntry(state, entry), pending: 'quiet' }
        }
        case 'encouragement': {
          const entry: TranscriptEntry = {
            kind: 'encouragement',
            objectiveId,
            text: action.text ?? '',
          }
          // The success-after-struggle capstone lands AFTER completion: route
          // it into the capstone for the just-completed objective. If the
          // objective is somehow still active, append to its transcript.
          // Belonging to neither → a too-late (stale) encouragement, dropped.
          if (state.capstone?.objectiveId === objectiveId) {
            return {
              ...state,
              capstone: {
                objectiveId,
                entries: [...state.capstone.entries, entry],
              },
            }
          }
          if (state.transcript[0]?.objectiveId === objectiveId) {
            return { ...state, ...pushEntry(state, entry), pending: 'quiet' }
          }
          return { ...state, pending: 'quiet' }
        }
        default:
          // silent / budget_exceeded / error — the model ran or was
          // suppressed but produced no learner-facing entry; resolve any
          // pending "looking…" beat back to quiet.
          return { ...state, pending: 'quiet' }
      }
    }
    case 'mentor.pending':
      // The lab-owned "AI looking…" beat — a real relay, a model call is
      // coming. Stale for a done objective → ignore.
      if (state.completedObjectiveIds.has(action.objectiveId)) return state
      return { ...state, pending: 'looking' }
    case 'capstone.cleared':
      // The provider's short hold timer fired (or it was superseded).
      return { ...state, capstone: null }
    case 'command.observed':
      return { ...state, commandCount: state.commandCount + 1 }
    case 'spec.replaced':
      return initialState(action.spec, action.completed)
  }
}

/**
 * The assessment view-model the UI (lab's `PartAssessmentPage`) renders one
 * screen off (umbrella #1357): the current question, the progress / score
 * chrome, the pass/fail verdict, and the weak concepts a fail should
 * remediate. Derived purely from a {@link QuizState} by
 * {@link PartAssessmentController.view} so the page's control surface is a
 * thin wrapper and the derivation is unit-testable without React.
 *
 * @property status - the assessment's terminal / continue verdict,
 *   mirroring the engine's {@link QuizDecision} but with an `in-progress`
 *   value before the attempt settles. `pass` / `fail` are the screens;
 *   `in-progress` / `continue` mean "show the current question".
 * @property question - the current unanswered question to render (the
 *   engine's `selectNext` result), or `null` when the attempt is settled
 *   (`pass` / `fail`) or the bank is exhausted for the draw
 * @property asked - questions answered so far this attempt (the ladder /
 *   budget axis)
 * @property earned - weighted points banked so far this attempt
 *   (post-discount)
 * @property max - the fixed mastery envelope (the scoring denominator)
 * @property threshold - the absolute pass bar (`passThreshold × max`)
 * @property band - the current difficulty band (1–5) the ladder points at
 * @property maxQuestions - the hard question cap for this attempt
 * @property weakConcepts - the concepts the learner answered wrong at least
 *   once this attempt (`priorWrongs > 0`), in insertion order; the fail
 *   screen resolves each to a remediation link. Empty on a clean pass.
 */
export interface QuizView {
  status: 'in-progress' | QuizDecision
  question: QuizQuestion | null
  asked: number
  earned: number
  max: number
  threshold: number
  band: number
  maxQuestions: number
  weakConcepts: NodeId[]
}

/**
 * The control surface the assessment UI drives one screen off (umbrella
 * #1357): the {@link QuizView} plus the `submit` and `retry` actions the
 * page wires to the {@link QuizEngine}. Kept a named interface (not inline
 * on the page) so the `QuizPhase` component types against it and a test can
 * construct one without React.
 *
 * @property view - the current assessment view-model
 * @property submit - grade + apply the learner's submission to the current
 *   question and advance the engine. A no-op when the attempt is already
 *   settled. Grading is deterministic ({@link QuizEngine.grade}) — no LLM.
 * @property retry - start a fresh-draw re-attempt after a fail (no
 *   cooldown): reshuffles from the bank and resets the ladder.
 */
export interface QuizControl {
  view: QuizView
  submit: (submission: QuizSubmission) => void
  retry: () => void
}

/**
 * Orchestration helpers for the Part-boundary CAT assessment (umbrella
 * #1357). Statics per `typescript.md` §"Functions belong on classes": the
 * vocabulary of "driving the engine from a Part's assessment bank", tied to
 * no instance. Relocated off the retired per-chapter `QuizPhaseController`
 * — the engine ({@link QuizEngine}) is unchanged; only the feed (a Part's
 * consolidated bank + concept SET, rather than one chapter's) and the view
 * (which now surfaces `weakConcepts` for remediation) differ.
 */
export class PartAssessmentController {
  /**
   * Build the initial {@link QuizState} for a Part's assessment. Resolves
   * the opening band (the assessment's `startBand` override, else the
   * config's operator-pinned default) and delegates to
   * {@link QuizEngine.init}, feeding the whole consolidated `questions`
   * bank and the Part's `concepts` SET (the draw rotates across it — see
   * {@link QuizEngine.drawConcept}).
   *
   * @param assessment - the Part's consolidated assessment (its
   *   `questions` + `concepts` + optional `startBand`)
   * @param config - the operator-pinned engine configuration
   * @param graph - the resolved course-graph region for predecessor draws
   * @returns the fresh {@link QuizState}
   */
  static initState(
    assessment: PartAssessment,
    config: QuizConfig,
    graph: CourseGraph,
  ): QuizState {
    const startBand = assessment.startBand ?? config.startBand
    return QuizEngine.init(
      { ...config, startBand },
      { questions: assessment.questions },
      graph,
      assessment.concepts,
    )
  }

  /**
   * Build the {@link QuizView} the UI renders from a {@link QuizState}. The
   * verdict is derived from the state via {@link QuizEngine.decision}: an
   * unsettled attempt (`continue`) shows the engine's `selectNext` draw
   * with `status: 'in-progress'`; once `pass` / `fail` the question is
   * `null` (the UI shows the verdict screen). `weakConcepts` is every
   * concept with a recorded prior-wrong this attempt, in insertion order —
   * the fail screen's remediation source.
   *
   * NOTE: {@link QuizEngine.selectNext} consumes `config.rng()` when the
   * drawn cell holds more than one unused item, so a caller that renders
   * off `view` should memoise on the `QuizState` identity (the lab page
   * does) rather than recomputing the view every render.
   *
   * @param state - the current assessment engine state
   * @returns the assessment view-model
   */
  static view(state: QuizState): QuizView {
    const decision = QuizEngine.decision(state)
    const settled = decision === 'pass' || decision === 'fail'
    const weakConcepts = [...state.priorWrongs]
      .filter(([, n]) => n > 0)
      .map(([concept]) => concept)
    return {
      status: settled ? decision : 'in-progress',
      question: settled ? null : QuizEngine.selectNext(state),
      asked: state.asked,
      earned: state.earned,
      max: state.config.max,
      threshold: QuizEngine.threshold(state.config),
      band: state.band,
      maxQuestions: state.config.maxQuestions,
      weakConcepts,
    }
  }
}
